import React, {createRef, PropsWithChildren, ReactNode, RefObject} from "react";
import {Button} from "primereact/button";
import {MessageType} from "../log/MessageInterface";
import {I18nGlobals} from "../translation/i18nInterface";
import Icon from "./Icon";
import FunctionHelper from "../helper/FunctionHelper";
import PortalPureComponent from "./PortalPureComponent";

const FADE_TIMEOUT = 500;

interface MessageProperties extends I18nGlobals {
	type?: MessageType;
	closeable?: boolean;
	children: ReactNode;
	stacktrace?: ReactNode;
	timeout?: number;
	fade?: boolean;
	visible?: boolean;
	onClose?: () => void;
	className?: string;
	showStacktrace?: boolean;
}

interface MessageState {
	visible?: boolean;
	showStacktrace: boolean;
}

export default class MessageBox extends PortalPureComponent<PropsWithChildren<MessageProperties>, MessageState> {
	private messageElement: RefObject<HTMLDivElement>;

	/**
	 * Constructs a {@link MessageBox} component
	 */
	constructor(props: Readonly<PropsWithChildren<MessageProperties>>) {
		super(props);

		this.state = {
			visible: !FunctionHelper.exists(props.visible) ? true : props.visible,
			showStacktrace: FunctionHelper.applyValue(props.showStacktrace, false)
		};

		this.messageElement = createRef<HTMLDivElement>();
		this.close = this.close.bind(this);
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<MessageProperties>, prevState: Readonly<MessageState>, snapshot?: any
	): Promise<void> {
		if (prevProps.visible !== this.props.visible) {
			this.setState({
				visible: this.props.visible
			});
		}
	}

	private async close() {
		if (this.messageElement.current === null || !this.state.visible) {
			return;
		}

		if (this.props.fade) {
			this.messageElement.current.classList.remove("in");

			await new Promise<void>((resolve) => {
				setTimeout(resolve, FADE_TIMEOUT);
			});
		}

		this.setState({visible: false});

		if (FunctionHelper.exists(this.props.onClose)) {
			this.props.onClose!();
		}
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		if (FunctionHelper.exists(this.props.timeout)) {
			setTimeout(() => this.close(), this.props.timeout! + FADE_TIMEOUT);
		}

		if (this.props.fade) {
			await new Promise<void>((resolve) => {
				setTimeout(resolve, 1);
			});

			if (this.messageElement.current === null) {
				return;
			}

			this.messageElement.current.classList.add("in");
		}
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		return (
			<div style={this.state.visible || !FunctionHelper.exists(this.state.visible) ? {} : {display: "none"}}
				 className={
					 "message message-" + this.props.type + (this.props.fade ? " fade" : "") +
					 " " + this.props.className
				 }
				 ref={this.messageElement}>
				<div className="message-header">
					<div className="message-icon">
						<Icon icon={"icon-" + this.props.type}/>
					</div>
					<div className="message-content">
						{this.props.children}
					</div>
					<div className="message-buttons">
						{FunctionHelper.exists(this.props.stacktrace) &&
							<Button className="message-button"
									icon={"icon icon-angle-" + (this.state.showStacktrace ? "down" : "left")}
									onClick={() => this.setState({showStacktrace: !this.state.showStacktrace})}/>
						}
						{this.props.closeable &&
							<Button className="message-button" icon="icon icon-close" onClick={() => this.close()}/>
						}
					</div>
				</div>
				{FunctionHelper.exists(this.props.stacktrace) &&
					<div className="message-body" hidden={!this.state.showStacktrace}>
						{this.props.stacktrace}
					</div>
				}
			</div>
		);
	}
}